import { datadogLogs, type StatusType, type LogsInitConfiguration } from '@datadog/browser-logs';
import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum';
import { reactPlugin, addReactError as datadogAddReactError } from '@datadog/browser-rum-react';
import type { UserID } from '@common/ids';
import EnvironmentConfig from '@util/config-environment';

type DatadogService = 'team-manager-web' | 'web-widgets';
interface DatadogOptions {
    service: DatadogService;
}

interface DatadogRumOptions extends DatadogOptions {}

interface DatadogLogOptions extends DatadogOptions {}

export type DatadogInitOptions = DatadogOptions & DatadogRumOptions & DatadogLogOptions;

interface DatadogSettings {
    rumInitialized: boolean;
    logsInitialized: boolean;
}

let settings: DatadogSettings = {
    rumInitialized: false,
    logsInitialized: false,
};

/**
 * Used to update the current settings. This is for testing only.
 * @param updatedSettings
 */
export function updateSettings(updatedSettings: Partial<DatadogSettings>) {
    settings = { ...settings, ...updatedSettings };
}

/**
 * Used to get the current settings. This is for testing only.
 */
export function getSettings() {
    return settings;
}

/**
 * Initializes Datadog observability based on the provided options.
 *
 * This function is meant to be used internally by our observability system.
 * Do not import and use this function directly.
 * @see Instead, use `initObservability` in src/util/observability/index.ts.
 */
export function init(options: DatadogInitOptions): boolean {
    initializeRUM(options);
    initializeLogs(options);

    return settings.logsInitialized || settings.rumInitialized;
}

export function setUser({ id, email }: { id: UserID; email?: string }) {
    const userInfo = { id, email };

    if (settings.logsInitialized) datadogLogs.setUser(userInfo);
    if (settings.rumInitialized) datadogRum.setUser(userInfo);
}

/**
 * Adds a custom action to Datadog RUM.
 *
 * This function is meant to be used internally by our observability system.
 * Do not import and use this function directly.
 * @see Instead, use `addAction` in src/util/observability/index.ts.
 */
export function addAction(name: string, context?: Record<string, unknown>) {
    datadogRum.addAction(name, context);
}

export function clearUser() {
    if (settings.logsInitialized) datadogLogs.clearUser();
    if (settings.rumInitialized) datadogRum.clearUser();
}
export const ACTION_NAME_ATTRIBUTE = 'data-observability-action-name';

function getRumConfig(
    service: DatadogService,
): Pick<
    RumInitConfiguration,
    | 'service'
    | 'clientToken'
    | 'applicationId'
    | 'env'
    | 'version'
    | 'sessionSampleRate'
    | 'sessionReplaySampleRate'
    | 'usePartitionedCrossSiteSessionCookie'
    | 'trackResources'
    | 'actionNameAttribute'
> {
    // Currently widgets and TM Web share the same config files,
    // so using the service name to determine which env variables to use.
    if (service === 'web-widgets') {
        return {
            service,
            clientToken: EnvironmentConfig.WIDGETS_RUM_CLIENT_TOKEN ?? '',
            applicationId: EnvironmentConfig.WIDGETS_RUM_APPLICATION_ID ?? '',
            env: process.env.NODE_ENV,
            version: EnvironmentConfig.VERSION_NUMBER ?? 'unknown-version',
            sessionSampleRate: Number(EnvironmentConfig.WIDGETS_RUM_SESSION_SAMPLE_RATE) || 0,
            sessionReplaySampleRate: Number(EnvironmentConfig.WIDGETS_RUM_SESSION_REPLAY_SAMPLE_RATE) || 0,
            usePartitionedCrossSiteSessionCookie: true,
            trackResources: true,
            actionNameAttribute: ACTION_NAME_ATTRIBUTE,
        };
    } else {
        return {
            service,
            clientToken: EnvironmentConfig.RUM_CLIENT_TOKEN ?? '',
            applicationId: EnvironmentConfig.RUM_APPLICATION_ID ?? '',
            env: process.env.NODE_ENV,
            version: EnvironmentConfig.VERSION_NUMBER ?? 'unknown-version',
            sessionSampleRate: Number(EnvironmentConfig.RUM_SESSION_SAMPLE_RATE) || 0,
            sessionReplaySampleRate: Number(EnvironmentConfig.RUM_SESSION_REPLAY_SAMPLE_RATE) || 0,
            trackResources: true,
            actionNameAttribute: ACTION_NAME_ATTRIBUTE,
        };
    }
}

function initializeRUM(options: DatadogRumOptions) {
    if (!settings.rumInitialized) {
        try {
            datadogRum.init({
                ...getRumConfig(options.service),
                plugins: [reactPlugin({ router: true })],
            });
            settings.rumInitialized = true;
        } catch (e) {
            // Allow printing to console to be able to know that Datadog is not initialized.
            // eslint-disable-next-line no-console
            console.error('Error initializing Datadog RUM:', e);
        }
    }
}

function getLogsConfig(
    service: DatadogService,
): Pick<
    LogsInitConfiguration,
    'service' | 'clientToken' | 'env' | 'version' | 'sessionSampleRate' | 'usePartitionedCrossSiteSessionCookie'
> {
    // Currently widgets and TM Web share the same config files,
    // so using the service name to determine which env variables to use.
    if (service === 'web-widgets') {
        return {
            service,
            clientToken: EnvironmentConfig.WIDGETS_LOGS_CLIENT_TOKEN ?? '',
            env: process.env.NODE_ENV,
            version: EnvironmentConfig.VERSION_NUMBER ?? 'unknown-version',
            sessionSampleRate: Number(EnvironmentConfig.WIDGETS_LOGS_SESSION_SAMPLE_RATE) || 0,
            usePartitionedCrossSiteSessionCookie: true,
        };
    } else {
        return {
            service,
            clientToken: EnvironmentConfig.LOGS_CLIENT_TOKEN ?? '',
            env: process.env.NODE_ENV,
            version: EnvironmentConfig.VERSION_NUMBER ?? 'unknown-version',
            sessionSampleRate: Number(EnvironmentConfig.LOGS_SESSION_SAMPLE_RATE) || 0,
        };
    }
}

function initializeLogs(options: DatadogLogOptions) {
    if (!settings.logsInitialized) {
        try {
            datadogLogs.init({
                ...getLogsConfig(options.service),
                forwardErrorsToLogs: true,
            });

            settings.logsInitialized = true;
        } catch (e) {
            // Allow printing to console to be able to know that Datadog is not initialized.
            // eslint-disable-next-line no-console
            console.error('Error initializing Datadog logging:', e);
        }
    }
}

/**
 * Adds an error to Datadog RUM.
 *
 * This function is meant to be used internally by our observability system.
 * Do not import and use this function directly.
 * @see Instead, use `addError` in src/util/observability/index.ts.
 */
export function addError(options: { error: unknown; context?: Record<string, unknown> }) {
    datadogRum.addError(options.error, options.context);
}

/**
 * Adds a React render error to Datadog RUM with the component stack attached.
 *
 * This function is meant to be used internally by our observability system.
 * Do not import and use this function directly.
 * @see Instead, use `addReactError` in src/util/observability/index.ts.
 */
export function addReactError(error: Error, info: { componentStack: string }, context?: Record<string, unknown>) {
    // Datadog's addReactError takes no context param; it reads a `dd_context` property off the error instead.
    if (context) {
        Object.assign(error, { dd_context: context });
    }
    datadogAddReactError(error, info);
}

/**
 * Logs a message to Datadog Logs.
 *
 * This function is meant to be used internally by our observability system.
 * Do not import and use this function directly.
 * @see Instead, use `log` in src/util/observability/index.ts.
 *
 * @param level
 * @param message
 * @param context
 * @param error
 */
export function log(level: StatusType, message: string, context?: object, error?: Error): void {
    datadogLogs.logger.log(message, context, level, error);
}
